import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { SizingItemsType, SlabFloorType } from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';
import {
  getValidSizingAttributeOptions,
  isValidSizingItem
} from 'utils/sizing';

type SlabFloorProps = {
  floor: SlabFloorType;
  updateFn: (floorId: string, updates: Partial<SlabFloorType>) => void;
  duplicateFn: (floorId: string) => void;
  deleteFn: (floorId: string) => void;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
};

export default function SlabFloorComponent({
  floor,
  updateFn,
  duplicateFn,
  deleteFn,
  updateDefaultSizingFn
}: SlabFloorProps) {
  const defaultValues = useAppSelector(
    (state) => state.data.defaultSizingItems.slabFloor
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);
  const sizingItemType = 'slabFloors';
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => floor[key] !== defaultValues[key]);

  const handleUpdateWithValidation = (changes: Partial<SlabFloorType>) => {
    const newFloor: SlabFloorType = {
      ...floor,
      ...changes
    };
    const isValid = isValidSizingItem(sizingItemType, newFloor);

    updateFn(floor.uniqueId, {
      ...changes,
      valid: isValid
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(floor.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(floor.uniqueId);
    setDeleteQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      slabFloor: {
        ...floor,
        uniqueId: undefined,
        valid: undefined,
        exposedEdgeFeet: undefined,
        feetBelowGrade: 1
      }
    });
  };

  const classes = clsx(
    'flex flex-col gap-3 rounded-xl border-2 bg-gray-100',
    floor.valid ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div className={classes}>
      <div
        key={floor.uniqueId}
        className='flex w-full flex-col divide-y divide-gray-300 rounded-lg bg-gray-200 px-5'
        ref={animate}
      >
        <div className='flex min-h-[54px] items-center gap-5'>
          <div className='font-semibold'>Slab Floor</div>
          <div className='w-[170px]'>
            <FormInput
              value={floor.exposedEdgeFeet}
              prefix='Exposed Edge: '
              placeholder='Exposed Edge (feet)'
              type='number'
              suffix=' ft'
              min={1}
              onChange={(value) =>
                handleUpdateWithValidation({ exposedEdgeFeet: value })
              }
              required
            />
          </div>
          {!floor.valid && (
            <span className='text-red-500'>Invalid Attributes!</span>
          )}
          <div className='ml-auto flex items-center gap-2'>
            <button
              className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
              type='button'
              onClick={toggleAdvancedOptions}
            >
              <span>Advanced</span>
              {showAdvancedOptions ? (
                <ChevronUpIcon className='h-5 w-5' />
              ) : (
                <ChevronDownIcon className='h-5 w-5' />
              )}
              <Tooltip
                text={
                  showAdvancedOptions
                    ? 'Hide Advanced Options'
                    : 'Show Advanced Options'
                }
              />
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDuplicateClick}
            >
              <DocumentDuplicateIcon className='h-5 w-5' />
              <Tooltip text='Duplicate Floor' />
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDeleteClick}
              onMouseLeave={() => setDeleteQueued(false)}
            >
              <TrashIcon className='h-5 w-5' />
              <Tooltip
                text={deleteQueued ? 'Click Again to Confirm' : 'Delete Floor'}
              />
            </button>
          </div>
        </div>
        {showAdvancedOptions && (
          <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
            <FormInput
              value={floor.slabInsulationArrangement}
              label='Insulation Arrangement'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                floor,
                'slabInsulationArrangement'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  slabInsulationArrangement: value,
                  insulationValue: value === 'None' ? 'None' : 'R-10'
                });
              }}
              required
              preventDeselection
            />
            {floor.slabInsulationArrangement !== 'None' && (
              <FormInput
                value={floor.insulationValue}
                label='Insulation Value'
                type='select'
                options={getValidSizingAttributeOptions(
                  sizingItemType,
                  floor,
                  'insulationValue'
                )}
                onChange={(value) => {
                  handleUpdateWithValidation({
                    insulationValue: value
                  });
                }}
                required
                preventDeselection
              />
            )}
            <FormInput
              value={floor.soilType}
              label='Soil Type'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                floor,
                'soilType'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  soilType: value
                });
              }}
              required
              preventDeselection
            />
            {isModified && (
              <div className='flex w-full justify-end'>
                <button
                  className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                  type='button'
                  onClick={setAsDefault}
                >
                  <span>Set as Default</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
